import type { ISelectOptions } from "src/@types/select";

import type { ExtendedColumnDef } from "components/Datatable";

import {
  ACCOUNT_FORM_MODE,
  type AccountFormMode,
} from "./components/account-form/types";
import type {
  Account,
  AccountStatusFilter,
} from "@/services/dtos/store-bank-account.dto";

export type StoreSelectOption = ISelectOptions[number];

export interface StatusFilterOption {
  value: AccountStatusFilter;
  label: string;
}

export const DEFAULT_STATUS_FILTER: AccountStatusFilter = "active";

export const STATUS_FILTER_OPTIONS: StatusFilterOption[] = [
  { value: "active", label: "Ativas" },
  { value: "inactive", label: "Inativas" },
  { value: "all", label: "Todas" },
];

export const ACTIVE_LABEL = {
  YES: "Sim",
  NO: "Não",
} as const;

export function formatActiveLabel(active: boolean): string {
  if (active) return ACTIVE_LABEL.YES;
  return ACTIVE_LABEL.NO;
}

export const PAGE_TITLE = "Contas Correntes das Lojas";
export const PAGE_DESCRIPTION =
  "Consulte as contas correntes bancárias cadastradas por loja.";

export const STORE_SELECT_LABEL = "Loja";
export const STORE_SELECT_PLACEHOLDER = "Selecione uma loja";
export const STATUS_SELECT_LABEL = "Status";
export const INCLUDE_BUTTON_LABEL = "Incluir";
export const STORE_VIEW_BUTTON_LABEL = "Ver na loja";
export const EDIT_BUTTON_LABEL = "Editar";

/** O `size="sm"` do Button tem 36px e deixa a linha da tabela alta demais. */
export const ROW_ACTION_BUTTON_CLASS = "h-7 px-2 text-xs";
export const ROW_ACTION_ICON_CLASS = "h-3.5 w-3.5 mr-1.5";

export const STORE_LABEL_ID = "store-bank-account-store-label";
export const STATUS_LABEL_ID = "store-bank-account-status-label";

export const EMPTY_STATE_MESSAGES = {
  NO_STORE_SELECTED: "Selecione uma loja para listar as contas correntes.",
  NO_RESULTS: "Nenhuma conta corrente encontrada para os filtros selecionados.",
} as const;

export const TABLE_COLUMN_LABELS = {
  BANK_NUMBER: "Cód. do banco",
  BANK_NAME: "Nome do banco",
  AGENCY: "Agência",
  SEQUENCE: "Seq",
  ACCOUNT: "Conta",
  ACTIVE: "Ativo",
  ACTIONS: "Ações",
} as const;

export const STORE_BANK_ACCOUNT_COLUMNS: ExtendedColumnDef<Account>[] = [
  {
    id: "bankNumber",
    header: TABLE_COLUMN_LABELS.BANK_NUMBER,
    accessorFn: (row) => row.bank_number,
  },
  {
    id: "bankName",
    header: TABLE_COLUMN_LABELS.BANK_NAME,
    accessorFn: (row) => row.bank_name,
  },
  {
    id: "agency",
    header: TABLE_COLUMN_LABELS.AGENCY,
    accessorFn: (row) => row.agency,
  },
  {
    id: "sequence",
    header: TABLE_COLUMN_LABELS.SEQUENCE,
    accessorFn: (row) => row.sequence,
  },
  {
    id: "account",
    header: TABLE_COLUMN_LABELS.ACCOUNT,
    accessorFn: (row) => row.account,
  },
  {
    id: "active",
    header: TABLE_COLUMN_LABELS.ACTIVE,
    accessorFn: (row) => formatActiveLabel(row.active),
  },
];

export interface AccountFormModalState {
  open: boolean;
  mode: AccountFormMode;
  account: Account | null;
}

export const CLOSED_ACCOUNT_FORM_MODAL_STATE: AccountFormModalState = {
  open: false,
  mode: ACCOUNT_FORM_MODE.CREATE,
  account: null,
};

export function buildCreateAccountModalState(): AccountFormModalState {
  return { open: true, mode: ACCOUNT_FORM_MODE.CREATE, account: null };
}

export function buildEditAccountModalState(
  account: Account,
): AccountFormModalState {
  return { open: true, mode: ACCOUNT_FORM_MODE.EDIT, account };
}

export function resolveAccountFormStoreCode(
  modalState: AccountFormModalState,
  selectedStoreCode: number | null,
): number | null {
  if (modalState.mode === ACCOUNT_FORM_MODE.EDIT && modalState.account) {
    return modalState.account.store_code;
  }
  return selectedStoreCode;
}

export interface StoreViewModalState {
  open: boolean;
  storeCode: number | null;
}

export const CLOSED_STORE_VIEW_MODAL_STATE: StoreViewModalState = {
  open: false,
  storeCode: null,
};

export function buildOpenStoreViewModalState(
  storeCode: number,
): StoreViewModalState {
  return { open: true, storeCode };
}
